import type { SerializedError } from '@reduxjs/toolkit'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import ApplyDialogV2 from '@core/components/ApplyDialogV2'
import {
    useGetApplyV2SettingsByIdQuery,
    useGetProfileV2ApplyQuery,
    useInitApplyV2Mutation,
    usePutApplyV2StepMutation,
} from '@core/app/api/applyJobV2Api'
import type {
    ApplyV2SettingsSection,
    ApplyV2SettingsSectionName,
    ApplyV2TempProfileData,
    GetApplyV2ProfileResponse,
    GetApplyV2SettingsResponse,
} from '@core/app/types/applyV2Type'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import type { PostData, PostInfo } from '@core/app/types/postsApiType'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import { createContext, useContext, useState, ReactNode, useCallback, useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { useGetPostInfoQuery } from '@core/app/api/postsApi'
import useLocalStorageStore from '@core/utils/hooks/useLocalStorageStore'
import type { IApplyDialogV2LocalStorage } from '@core/app/types'

type ApplyV2ModalContextType = {
    isOpen: boolean
    isExpanded: boolean
    onResizeApply: () => void
    onOpenApply: (component?: ReactNode) => void
    onCloseApply: () => void
    storageData: IApplyDialogV2LocalStorage | null
    content: ReactNode | null
    error: boolean
    settingsData: GetApplyV2SettingsResponse | null | undefined
    settingsError: FetchBaseQueryError | SerializedError | undefined | null
    isSettingsLoading: boolean
    settingsRefetch: () => void
    // isPostLoading: boolean
    postRefetch: () => void
    isPostFetching: boolean
    isSettingsFetching: boolean
    postData: PostData | null
    onSelectStep: (step: ApplyV2SettingsSectionName | null) => void
    initData: GetApplyV2ProfileResponse | undefined | null
    initError: FetchBaseQueryError | SerializedError | undefined | null
    isInitError: boolean
    isInitLoading: boolean
    postProfileData: GetApplyV2ProfileResponse | undefined | null
    postProfileError: FetchBaseQueryError | SerializedError | undefined | null
    isPostProfileFetching: boolean
    isPostProfileSuccess: boolean
    postProfileRefetch: () => void
    isPostProfileLoading: boolean
    onNextStep: () => void
    navItems: (ApplyV2SettingsSection & { title: string })[]
    isFinalStep: boolean
    setIsFinalStep: (value: boolean) => void
    isPostApplied: boolean
}

const ApplyV2ModalContext = createContext<ApplyV2ModalContextType>({
    isOpen: false,
    isExpanded: false,
    onOpenApply: () => {
        console.info(`ApplyV2ModalContext openModal() for ${window?.location?.href} is not provided`)
    },
    onCloseApply: () => {
        console.info(`ApplyV2ModalContext closeModal() for ${window?.location?.href} is not provided`)
    },
    onResizeApply: () => {
        console.info(`ApplyV2ModalContext resizeModal() for ${window?.location?.href} is not provided`)
    },
    storageData: null,
    content: null,
    error: false,
    settingsData: null,
    settingsError: null,
    isSettingsLoading: false,
    settingsRefetch: () => {
        console.info(`ApplyV2ModalContext settingsRefetch() ${window?.location?.href} is not provided`)
    },
    // isPostLoading: false,
    postRefetch: () => {
        console.info(`ApplyV2ModalContext postRefetch() ${window?.location?.href} is not provided`)
    },
    isPostFetching: false,
    isSettingsFetching: false,
    postData: null,
    onSelectStep: (step: ApplyV2SettingsSectionName | null) => {
        console.info(`ApplyV2ModalContext onSelectStep() for ${window?.location?.href} is not provided`)
    },
    initData: null,
    postProfileData: null,
    postProfileError: null,
    isPostProfileFetching: false,
    isPostProfileSuccess: false,
    postProfileRefetch: () => {
        console.info(`ApplyV2ModalContext refetch() for ${window?.location?.href} is not provided`)
    },
    initError: null,
    isInitError: false,
    isInitLoading: false,
    isPostProfileLoading: false,
    onNextStep: () => {},
    navItems: [],
    isFinalStep: false,
    setIsFinalStep: () => {
        console.info(`ApplyV2ModalContext setIsFinalStep() for ${window?.location?.href} is not provided`)
    },
    isPostApplied: false,
})

export const ApplyV2ModalProvider = ({ children }: { children: ReactNode }) => {
    const { id } = useParams()
    const { getTitle } = useApplyV2NavTranslations()
    const location = useLocation()
    const [isOpen, setIsOpen] = useState(false)
    const [content, setContent] = useState<ReactNode | null>(null)
    const [isExpanded, setIsExpanded] = useState(false)
    const [error, setError] = useState<boolean>(false)
    const numberId = useMemo(() => {
        const n = Number(id)
        return isNaN(n) ? 0 : n
    }, [id])
    const key = useMemo(() => `apply_dialog_v2-${numberId}` as const, [numberId])
    const [storageData, setStorageData, removeStorageData] = useLocalStorageStore(key)
    const [serviceCode] = useLocalStorageStore('service_code')

    const {
        data: settingsData,
        error: settingsError,
        isLoading: isSettingsLoading,
        isFetching: isSettingsFetching,
        refetch: settingsRefetch,
        currentData,
    } = useGetApplyV2SettingsByIdQuery({ id: numberId }, { skip: !numberId || !isOpen })

    const {
        data: postData,
        error: postError,
        // isLoading: isPostLoading,
        isFetching: isPostFetching,
        isSuccess: isPostSuccess,
        isError: isPostError,
        refetch: postRefetch,
    } = useGetPostInfoQuery(
        {
            id: numberId,
        },
        { skip: !numberId || !isOpen }
    )

    const [initApplyV2, { data: initData, error: initError, isError: isInitError, isLoading: isInitLoading }] =
        useInitApplyV2Mutation()

    const {
        data: postProfileData,
        error: postProfileError,
        isFetching: isPostProfileFetching,
        isSuccess: isPostProfileSuccess,
        refetch: postProfileRefetch,
    } = useGetProfileV2ApplyQuery(
        { uid: initData?.data?.uid },
        {
            skip: !initData?.data?.uid || !isOpen || !!initData,
        }
    )

    const [putStepRequest] = usePutApplyV2StepMutation()

    const navItems = useMemo(() => {
        return Object.values(settingsData?.steps || {})
            .filter((it) => it.visible)
            .sort((a, b) => a.order - b.order)
            .map((item) => ({ ...item, title: getTitle(item.name, item.name) }))
    }, [settingsData])

    useEffect(() => {
        if (postData?.data?.ad?.AdID && storageData) {
            const title = decodeHtmlEntitiesRecursive(postData?.data?.ad?.JobTitle ?? '')
            // console.log('useEffect 1 - OK', storageData.id, postData.data.ad.AdID)
            if (storageData.id === postData.data.ad.AdID) {
                if (storageData.title !== title) {
                    setStorageData({
                        ...storageData,
                        title: title,
                        expiredDate: postData?.data?.ad?.Expire ?? '',
                    })
                }
            }
        }
    }, [postData])

    useEffect(() => {
        if (initData?.data?.uid && storageData && storageData?.postInitUid !== initData?.data?.uid) {
            setStorageData({
                ...storageData,
                postInitUid: initData.data.uid,
                currentStep: storageData?.currentStep ?? initData?.data?.step ?? navItems[0]?.name ?? null,
            })
        }
    }, [initData, storageData, setStorageData])

    useEffect(() => {
        // console.log('useEffect 2', storageData)
        setIsExpanded(storageData?.expanded ?? false)
        setIsOpen(storageData?.opened ?? false)

        if (storageData?.id && storageData?.opened && storageData?.expanded && initApplyV2 && !initData) {
            initApplyV2({ id: numberId })
            console.log('initApplyV2() id: ', numberId)
        }
    }, [storageData?.id, storageData?.expanded, storageData?.opened, numberId])

    const setIsFinalStep = useCallback(
        (value: boolean) => {
            if (!storageData) {
                console.warn('[ApplyV2ModalContext] Cannot set isFinalStep: storageData is null.')
                return
            }
            setStorageData({ ...storageData, isFinalStep: value })
        },
        [storageData, setStorageData]
    )

    const onResizeApply = useCallback(() => {
        if (!storageData) {
            console.warn('[ApplyV2ModalContext] Cannot resize modal: storageData is null.')
            return
        }
        setStorageData({ ...storageData, expanded: !storageData.expanded })
    }, [storageData, setStorageData])

    // const onOpenApply = useCallback(
    //     (component?: ReactNode) => {
    //         setContent(component ?? null)
    //         setIsOpen(true)
    //         setStorageData({
    //             id: Number(id ?? storageData?.id ?? 0),
    //             opened: true,
    //             expanded: true,
    //             title: decodeHtmlEntitiesRecursive(storageData?.title ?? postData?.data?.ad?.JobTitle ?? ''),
    //             expiredDate: storageData?.expiredDate || postData?.data?.ad?.Expire || '',
    //             link: storageData?.link || settingsData?.externalLink,
    //         })
    //     },
    //     [storageData, postData?.data?.ad?.JobTitle, postData?.data?.ad?.Expire, settingsData?.externalLink, id]
    // )

    const onNextStep = useCallback(() => {
        if (!storageData) {
            console.warn('[ApplyV2ModalContext] Cannot go to next step: storageData is null.')
            return
        }
        if (!storageData?.postInitUid) {
            console.warn('[ApplyV2ModalContext] Cannot go to next step: postInitUid is null.')
            return
        }
        const currentIndex = navItems.findIndex((item) => item.name === storageData.currentStep)
        if (currentIndex < 0 || currentIndex >= navItems.length - 1) {
            console.warn('[ApplyV2ModalContext] No next step available.')
            return
        }
        const nextStep = navItems[currentIndex + 1].name
        setStorageData({ ...storageData, currentStep: nextStep })
        putStepRequest({ uid: storageData.postInitUid, step: nextStep })
    }, [storageData, navItems, putStepRequest])

    const onOpenApply = (component?: ReactNode) => {
        const title = decodeHtmlEntitiesRecursive(postData?.data?.ad?.JobTitle)
        console.log('onOpenApply()', title, storageData?.title)
        setContent(component ?? null)
        setStorageData({
            id: numberId,
            opened: true,
            expanded: true,
            title: title,
            expiredDate: postData?.data?.ad?.Expire ?? '',
            link: settingsData?.externalLink,
            currentStep:
                storageData?.currentStep ??
                initData?.data?.step ??
                postProfileData?.data?.step ??
                navItems[0]?.name ??
                null,
            postInitUid: initData?.data?.uid ?? null,
            isFinalStep: storageData?.isFinalStep ?? false,
            isPostApplied: storageData?.isPostApplied ?? false,
        })
    }

    const onCloseApply = useCallback(() => {
        // console.log('onCloseApply()')
        // setIsOpen(false)
        // setIsExpanded(false)
        setContent(null)
        removeStorageData()
    }, [])

    const onSelectStep = useCallback(
        (step: ApplyV2SettingsSectionName | null) => {
            if (!storageData) {
                console.warn('[ApplyV2ModalContext] Cannot select step: storageData is null.')
                return
            }
            setStorageData({ ...storageData, currentStep: step })
            if (step && storageData?.postInitUid) {
                putStepRequest({ uid: storageData.postInitUid, step: step })
            }
        },
        [storageData, setStorageData]
    )

    // console.log('ApplyV2ModalContext render')

    // useEffect(() => {
    //     if (dayjs(storageData?.job?.expiredDate).diff(dayjs(), 'day') < 0) {
    //         // closeModal()
    //         console.warn(`[ApplyV2ModalContext closeModal()] Modal expired: ${storageData?.job?.expiredDate}`)
    //     }
    // }, [storageData])

    return (
        <ApplyV2ModalContext.Provider
            value={{
                isOpen,
                isExpanded,
                onOpenApply,
                onCloseApply,
                onResizeApply,
                content,
                storageData,
                error,
                settingsError,
                settingsRefetch,
                settingsData,
                isSettingsLoading,
                // isPostLoading,
                postRefetch,
                isPostFetching,
                isSettingsFetching,
                postData: postData?.data ?? null,
                onSelectStep,
                initData,
                postProfileData: postProfileData ?? initData,
                postProfileError: postProfileError ?? initError,
                isPostProfileFetching,
                isPostProfileLoading: isInitLoading || isPostProfileFetching,
                isPostProfileSuccess: isPostProfileSuccess,
                postProfileRefetch,
                initError,
                isInitError,
                isInitLoading,
                onNextStep,
                navItems,
                isFinalStep: storageData?.isFinalStep ?? false,
                setIsFinalStep,
                isPostApplied: storageData?.isPostApplied ?? false,
            }}
        >
            {children}
            {isOpen && <ApplyDialogV2 />}
        </ApplyV2ModalContext.Provider>
    )
}

export const useApplyV2Context = () => useContext(ApplyV2ModalContext)
